import { ExternalScheduleContactsInputs } from '@components/schedules/dialogs/external/inputs/contacts';
import { ExternalScheduleDateInputs } from '@components/schedules/dialogs/external/inputs/date';
import { FormField } from '@components/ui/form';
import { FormItemWrapper } from '@components/ui/form/itemWrapper';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { RelatedSchedulesDialogInputsProps } from './types';
import { useDropdownsContext } from '@contexts/dropdowns';
import { useEffect, useMemo } from 'react';
import { useRescheduler } from '@contexts/rescheduler';
import { SelectInput } from '@components/ui/form/select/input';
import { InternalScheduleDateInputs } from '../../internal/inputs/date';
import { getFullName } from '@utils/textHelpers';

export const RelatedSchedulesDialogInputs = ({
  data,
  scheduleType,
  dateConfirmation,
  specialty,
  jobId,
  contactId,
  foremanId,
}: RelatedSchedulesDialogInputsProps) => {
  const { fieldUsers, setParams } = useDropdownsContext();
  const { removeSavedSchedule } = useRescheduler();
  const { control, setValue } = useFormContext<{
    forceOverlapping: boolean;
    relatedScheduleForemanId: string;
  }>();
  const { t } = useTranslation('app');

  useEffect(() => {
    setParams({ users: { specialty } });
    return () => setParams({ users: {} });
  }, [setParams, specialty]);

  useEffect(() => {
    if (foremanId && !contactId) {
      setValue('relatedScheduleForemanId', foremanId);
    }
  }, [contactId, foremanId, setValue]);

  const foremanOptions = useMemo(() => {
    const options = fieldUsers.options.map((el) => ({
      name: el.fullName || el.name || '',
      value: el.id,
    }));

    if (foremanId && !options.some((el) => el.value === foremanId)) {
      const foremanName =
        data.foreman?.fullName || getFullName(data.foreman?.firstName, data.foreman?.lastName);
      return [{ name: foremanName, value: foremanId }, ...options];
    }

    return options;
  }, [
    fieldUsers.options,
    foremanId,
    data.foreman?.fullName,
    data.foreman?.firstName,
    data.foreman?.lastName,
  ]);

  const renderDateInputs = () => {
    if (scheduleType === 'internal') {
      return (
        <InternalScheduleDateInputs data={data}>
          <FormField
            control={control}
            name="forceOverlapping"
            render={() => (
              <FormItemWrapper>
                <input type="hidden" />
              </FormItemWrapper>
            )}
          />
          <FormField
            control={control}
            name="relatedScheduleForemanId"
            defaultValue={foremanId || ''}
            render={({ field: { value, onChange } }) => (
              <FormItemWrapper label={t('forms.foreman.label')} select required>
                <SelectInput
                  name="relatedScheduleForemanId"
                  value={value}
                  onChange={(v) => {
                    if (!v && value) return;
                    removeSavedSchedule(data);
                    onChange(v);
                  }}
                  options={foremanOptions}
                  disabled={fieldUsers.disabled}
                  placeholder={t('forms.default.select.placeholder')}
                />
              </FormItemWrapper>
            )}
          />
        </InternalScheduleDateInputs>
      );
    }

    return <ExternalScheduleDateInputs data={data} />;
  };

  return (
    <>
      {renderDateInputs()}
      {dateConfirmation === 'external' && (
        <ExternalScheduleContactsInputs data={data} jobId={jobId} initialContactId={contactId} />
      )}
    </>
  );
};
